import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { setUserSettingThunk } from '../profile/profileSlice';
import { parseUserSetting } from '../profile/utils';
import {
  BREAK_DISPLAY_DISABLED,
  BREAK_DISPLAY_DISMISSED_BREAK,
} from '../profile/types';

/**
 * The user's break-display settings, read straight from the store so they stay live.
 *
 * These are deliberately not read through `usePersistedState`: the overlay is mounted once for
 * the whole session, and `usePersistedState` snapshots its value on first render. A snapshot
 * would leave the overlay hidden after the user clears these on the settings page, until they
 * reloaded the browser.
 */
export const useBreakDisplayUserSettings = () => {
  const dispatch = useAppDispatch();

  const dismissedBreak = useSelector((state: RootState) =>
    parseUserSetting<string>(
      state.profile.userSetting[BREAK_DISPLAY_DISMISSED_BREAK],
      '',
    ),
  );
  const disabled = useSelector((state: RootState) =>
    parseUserSetting<boolean>(
      state.profile.userSetting[BREAK_DISPLAY_DISABLED],
      false,
    ),
  );

  return {
    dismissedBreak,
    disabled,
    /** Records this break as dismissed, so a reload during it does not re-show the announcement. */
    dismissBreak: (breakKey: string) =>
      dispatch(
        setUserSettingThunk({
          key: BREAK_DISPLAY_DISMISSED_BREAK,
          value: JSON.stringify(breakKey),
        }),
      ),
    /** Opts the user out of break announcements until they reset it on the settings page. */
    disableBreakDisplay: () =>
      dispatch(
        setUserSettingThunk({
          key: BREAK_DISPLAY_DISABLED,
          value: JSON.stringify(true),
        }),
      ),
  };
};
